import React, { useState } from "react";
import { PhoneCall, Loader2, History } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { http } from "@/hooks/useAdmin";
import { fmtDate } from "@/components/admin/LeadViews";

export const OUTCOMES = [
  { key: "reached", label: "Reached", cls: "bg-emerald-50 text-emerald-700" },
  { key: "no_answer", label: "No answer", cls: "bg-slate-100 text-slate-600" },
  { key: "call_back", label: "Call back", cls: "bg-gold/15 text-[#8a6500]" },
  { key: "not_interested", label: "Not interested", cls: "bg-red-50 text-red-600" },
  { key: "converted", label: "Converted", cls: "bg-royal/10 text-royal" },
];
const outcomeMeta = (k) => OUTCOMES.find((o) => o.key === k);

export const OutcomeBadge = ({ lead }) => {
  const o = outcomeMeta(lead.last_outcome);
  if (!o) return null;
  return (
    <span data-testid={`lead-outcome-${lead.id}`} title={lead.last_call_at ? `Last call ${fmtDate(lead.last_call_at)}` : ""} className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold ${o.cls}`}>
      <PhoneCall className="h-3 w-3" /> {o.label}{lead.call_count > 1 ? ` · ${lead.call_count}` : ""}
    </span>
  );
};

export const CallHistory = ({ lead }) => {
  const [open, setOpen] = useState(false);
  const logs = [...(lead.call_logs || [])].reverse();
  if (!logs.length) return null;
  return (
    <div className="mt-2">
      <button data-testid={`lead-history-toggle-${lead.id}`} onClick={() => setOpen((v) => !v)} className="inline-flex items-center gap-1 text-[11px] font-semibold text-navy/60 hover:text-navy">
        <History className="h-3 w-3" /> {open ? "Hide" : "Show"} {logs.length} call{logs.length === 1 ? "" : "s"}
      </button>
      {open && (
        <ul data-testid={`lead-history-${lead.id}`} className="mt-2 space-y-1.5">
          {logs.map((l) => (
            <li key={l.id} className="rounded-lg bg-lightbg px-3 py-2 text-xs">
              <p className="font-semibold text-navy">{l.outcome_label}{l.follow_up_at ? ` · follow up ${l.follow_up_at}` : ""}</p>
              {l.note && <p className="mt-0.5 text-ink/70">{l.note}</p>}
              <p className="mt-0.5 text-[10px] text-ink/40">{fmtDate(l.created_at)}</p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export const LogCallButton = ({ lead, onLogged }) => {
  const [open, setOpen] = useState(false);
  const [outcome, setOutcome] = useState("reached");
  const [note, setNote] = useState("");
  const [followUp, setFollowUp] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await http.post(`/admin/leads/${lead.id}/calls`, { outcome, note: note || null, follow_up_at: followUp || null });
      onLogged(data);
      toast.success(`Logged: ${outcomeMeta(outcome).label}`);
      setOpen(false);
      setOutcome("reached");
      setNote("");
      setFollowUp("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not log call");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <button data-testid={`lead-logcall-${lead.id}`} onClick={() => setOpen(true)} className="inline-flex items-center gap-1 whitespace-nowrap rounded-full border border-navy/10 px-3 py-1.5 text-xs font-semibold text-navy hover:border-navy/40">
        <PhoneCall className="h-3 w-3" /> Log call
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="logcall-dialog" className="max-w-md rounded-3xl bg-white p-7 sm:rounded-3xl">
          <DialogTitle className="font-display text-xl font-bold text-navy">Log a call — {lead.name}</DialogTitle>
          <DialogDescription className="text-sm text-ink/60"><a href={`tel:${lead.mobile}`} className="text-royal">{lead.mobile}</a> · {lead.primary_goal || "General enquiry"}</DialogDescription>
          <form onSubmit={submit} className="mt-2 space-y-4">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wide text-ink/45">Outcome</p>
              <div className="mt-2 flex flex-wrap gap-2">
                {OUTCOMES.map((o) => (
                  <button type="button" key={o.key} data-testid={`logcall-outcome-${o.key}`} onClick={() => setOutcome(o.key)}
                    className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors ${outcome === o.key ? "bg-navy text-white" : `${o.cls} opacity-80 hover:opacity-100`}`}>{o.label}</button>
                ))}
              </div>
            </div>
            <textarea data-testid="logcall-note" value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="What was discussed? Next steps?" className="w-full rounded-xl border border-navy/10 bg-lightbg px-3 py-2 text-sm outline-none focus:border-royal" />
            <div>
              <label htmlFor="lc-fu" className="text-[11px] font-semibold uppercase tracking-wide text-ink/45">Follow-up date (optional)</label>
              <input id="lc-fu" type="date" data-testid="logcall-followup" value={followUp} onChange={(e) => setFollowUp(e.target.value)} className="mt-1 w-full rounded-xl border border-navy/10 px-3 py-2 text-sm outline-none focus:border-royal" />
            </div>
            <button type="submit" data-testid="logcall-save" disabled={saving} className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-gold px-5 py-3 text-sm font-bold text-navy disabled:opacity-60">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <PhoneCall className="h-4 w-4" />} Save call log
            </button>
            <p className="text-[11px] text-ink/45">Reached / Call back marks the enquiry Contacted; Converted marks it Converted. The note is also added to the contact's CRM timeline.</p>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};
